import { useTranslation } from "react-i18next";
import { Address, NewAddress, newAddressInitialValues } from "../../../models/Address";
import { useForm } from "react-hook-form";
import { Modal } from "../../Modal";
import { Fragment } from "react/jsx-runtime";
import { ReactiveFormInput } from "../../form/ReactiveFormInput";
import { ReactiveFormSelect } from "../../form/ReactiveFormSelect";
import { useEffect, useMemo, useState } from "react";
import states from "states-us";
import { AppDispatch, RootState } from "../../../state/store";
import { useDispatch, useSelector } from "react-redux";
import { getCountries } from "../../../i18n/countries";
import { createClientAddress, updateClientAddress } from "../../../state/addresses/actions";
import { Client } from "../../../models/Client";
import { stat } from "fs";
import { toast, ToastContainer } from "react-toastify";
import { Toast } from "../../Toast";
import { LoadingMask } from "../../LoadingMask";
import { ReactiveFormRadioButtonSelect } from "../../form/ReactiveFormRadioButtonSelect";

interface Props {
    info: {
        show: boolean;
        update: boolean;
        address?: Address;
        index?: number;
        client?: Client;
        canEditClient: boolean;
        isFirstAddress: boolean;
        isNewClient: boolean;
    };
    addAddress: (address: Address) => void;
    updateAddress: (address: Address, index: number) => void;
    onClose: () => void;
}

export const ClientAddressModal: React.FC<Props> = ({
    info,
    addAddress,
    updateAddress,
    onClose
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const isLoading = useSelector((state: RootState) => state.addresses.isLoading);
    const error = useSelector((state: any) => state.addresses.error);
    const [showAddressError, setShowAddressError] = useState(false);

    useEffect(() => {
        if (showAddressError) {
            Toast(
                t(error?.reason ?? "SOMETHING_WENT_WRONG"),
                t(error?.cause_info)
            );
        }
    }, [showAddressError, error, t]);

    const methods = useForm<Address>({
        defaultValues: newAddressInitialValues
    })
    const { handleSubmit, control, setValue, reset } = methods;

    const getStates = useMemo(() => {
        return states.map((state) => {
          return {
            label: state.name,
            value: state.abbreviation,
            key: state.abbreviation,
          };
        });
    }, []);

    useEffect(() => {
        info.address ? reset(info.address) : reset(newAddressInitialValues);

        if(!info.update && info.isFirstAddress) {
            setValue("is_main_address", "1");
        }
    }, [info.address, info.isFirstAddress, reset]);

    const onSubmit = (data: Address) => {
        if(info.isNewClient) {
            info.update ? updateAddress(data, Number(info.index)) : addAddress(data)
            closeModal()
        } else {
            if (info.update) {
                //TODO: update
                dispatch(updateClientAddress({editAddress: data, addressId: info.address?.id!, clientId: info.client?.id!})).then((e) => {
                    if (e.type === "client/updateClientAddress/rejected") {
                        setShowAddressError(true);
                    } else {
                        let address = (e.payload || []) as Address;
                        toast(t("SUCCESSFULLY_UPDATE_CLIENT_ADDRESS"));
                        updateAddress(address, Number(info.index))
                        closeModal();
                    }
                });
            } else {
                //Todo: add
                dispatch(createClientAddress({newAddress: data, clientId: info.client?.id!})).then((e) => {
                    if (e.type === "client/createClientAddress/rejected") {
                        setShowAddressError(true);
                    } else {
                        let address = (e.payload || []) as Address;
                        toast(t("SUCCESSFULLY_SAVED_CLIENT_ADDRESS"));
                        addAddress(address)
                        closeModal();
                    }
                });
            }
        }
    }

    const closeModal = () => {
        reset({});
        onClose();
    };

    return (<>
        <Modal
            isOpen={info.show}
            onClose={closeModal}
            label={info.update ? t("UPDATE_ADDRESS") : t("ADD_ADDRESS")}
            onSave={handleSubmit(onSubmit)}
            hideSaveButton={!info.canEditClient}
        >
            <div className="w-full flex flex-wrap">
                <Fragment>
                    {
                        !info.update && <div className="flex md:w-1/3">
                            <ReactiveFormRadioButtonSelect
                                control={control}
                                name={`is_main_address`}
                                label={t("SET_AS_MAIN_ADDRESS")}
                                options={[
                                    { label: t("YES"), value: "1" },
                                    { label: t("NO"), value: "0" },
                                ]}
                            />
                        </div>
                    }
                    <ReactiveFormInput
                        control={control}
                        className="md:w-1/3"
                        label={t("STREET_ADDRESS")}
                        name={`address_line_1`}
                        isDisabled={!info.canEditClient}
                        isRequired
                    />
                    <ReactiveFormInput
                        control={control}
                        className="md:w-1/3"
                        label={t("STREET_ADDRESS2")}
                        name={`address_line_2`}
                        isDisabled={!info.canEditClient}
                    />
                    <ReactiveFormInput
                        control={control}
                        className="md:w-1/3"
                        label={t("CITY")}
                        name={`city`}
                        isDisabled={!info.canEditClient}
                        isRequired
                    />
                    <ReactiveFormSelect
                        width="md:w-1/3"
                        name={`state`}
                        label={t("STATE")}
                        options={getStates}
                        control={control}
                        isDisabled={!info.canEditClient}
                        isRequired
                    />
                    <ReactiveFormSelect
                        width="md:w-1/2 lg:w-1/3"
                        name="country"
                        label={t("COUNTRY")}
                        options={getCountries()}
                        control={control}
                        isDisabled={!info.canEditClient}
                    />
                    <ReactiveFormInput
                      control={control}
                      className="md:w-1/3"
                      label={t("ZIP_CODE")}
                      name={`zip_code`}
                      isDisabled={!info.canEditClient}
                      isRequired
                    />
                </Fragment>
            </div>
        </Modal>
        <ToastContainer progressStyle={{ background: "#D4AF37" }} />
        { isLoading && <LoadingMask />}
    </>)

}